import type { CalendarItem } from '../../../shared/types';
import { platformName } from '../../../shared/platforms';
import { PlatformIcon } from '../ui';
import { PLATFORM_POST_STATUS_TONE, formatTime } from '../../lib/format';
import { cn } from '../../lib/utils';

const TONE_DOT: Record<'neutral' | 'info' | 'success' | 'warning' | 'danger', string> = {
  neutral: 'bg-subtle',
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
};

export interface CalendarItemPillProps {
  item: CalendarItem;
  timezone: string;
  onClick: () => void;
  /** Week view has room for a time badge; month cells stay bare. */
  showTime?: boolean;
  className?: string;
}

/** A single scheduled/published platform post, colored by platform brand. Owned by WEB-3. */
export function CalendarItemPill({ item, timezone, onClick, showTime, className }: CalendarItemPillProps) {
  const tone = PLATFORM_POST_STATUS_TONE[item.status];
  const when = item.scheduledAt ?? item.publishedAt;
  const label = item.title?.trim() || item.excerpt || platformName(item.platform);

  return (
    <button
      type="button"
      onClick={onClick}
      title={`${platformName(item.platform)} · ${label}`}
      className={cn(
        'flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-[11px] leading-tight',
        'transition-colors hover:bg-elevated focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring',
        className,
      )}
    >
      <PlatformIcon platform={item.platform} size="xs" variant="soft" className="shrink-0" />
      {showTime && when && (
        <span className="pap-tnum shrink-0 text-subtle">{formatTime(when, timezone)}</span>
      )}
      <span className="truncate flex-1 text-muted">{label}</span>
      <span className={cn('size-1.5 shrink-0 rounded-full', TONE_DOT[tone])} aria-hidden />
    </button>
  );
}
